import _ from 'lodash';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import modalStore, { ModalOption } from '@client/store/modalStore';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import { ModalPlugin } from './modalPlugin';
import { ModalActionButton, ModalCloseXButton } from './ModalButton';
import Carousel, {
    CarouselProvider,
    useCarouselContext,
} from '@app/haewao/client/view/common/Carousel';
import deviceStore from '@client/store/deviceStore';

/**
 * Record<type, Plugin> 형태의 플러그인 맵을 받아서 ModalContainer 컴포넌트를 생성하는 팩토리 함수
 * - 플러그인의 type → component 매핑으로 렌더링
 * - 앱별로 사용할 플러그인을 지정하여 전용 ModalContainer를 만들 수 있음
 */
export const createModalContainer = <
    TPlugins extends Record<string, ModalPlugin<string, any, any>>,
>(
    plugins: TPlugins,
) => {
    const componentMap = new Map(Object.values(plugins).map((p) => [p.type, p.component]));

    return observer(() => (
        <div className="invisible fixed h-0 w-0">
            {_.map(Array.from(modalStore.modalMap.entries()), ([modalId, obj]) => {
                const modalItem = obj?.modalItem;

                if (modalItem?.state !== 'opened' && modalItem?.state !== 'closing') {
                    return null;
                }

                const Component = componentMap.get(modalItem.option.type);

                if (!Component) {
                    return null;
                }

                return <Component key={modalId} modalId={modalId} option={modalItem.option} />;
            })}
        </div>
    ));
};

/**
 * Modal 컴포넌트 라이브러리
 * - 각 모달에서 조합하여 사용
 */

const Dialog = observer(
    ({
        modalId,
        children,
        className,
    }: {
        modalId: string;
        children: React.ReactNode;
        className?: string;
    }) => {
        const dialogRef = useRef<HTMLDialogElement>(null);
        const modalItem = modalStore.modalMap.get(modalId)?.modalItem;
        const option = modalItem?.option;

        useEffect(() => {
            const dialog = dialogRef.current;
            if (!dialog) {
                return;
            }

            modalStore.setModalElement(modalId, dialog);

            _.delay(() => {
                dialog.showModal();
                modalItem?.option.onOpen?.({ modalId });
            }, 50);

            const onClose = () => {
                modalStore.prepareClose(modalId);

                const returnValue = modalItem?.returnValue;
                if (modalItem?.onClose) {
                    modalItem.onClose(returnValue);
                }

                _.delay(() => {
                    modalStore.completeClose(modalId);
                }, 500);
            };

            const onCancel = (e: Event) => {
                if (modalItem?.option.preventClose) {
                    e.preventDefault();
                }
            };

            dialog.addEventListener('close', onClose);
            dialog.addEventListener('cancel', onCancel);

            return () => {
                dialog.removeEventListener('close', onClose);
                dialog.removeEventListener('cancel', onCancel);
            };
        }, [modalId]);

        return (
            <dialog
                ref={dialogRef}
                className={twMerge(
                    'modal select-none',
                    'modal-bottom',
                    'sm:modal-middle',
                    className,
                )}
            >
                <div
                    className={twMerge(
                        'modal-box container min-w-0 overflow-visible px-0 pt-6 pb-5',
                        option?.maxWidth
                            ? 'sm:max-w-[min(calc(100dvw-40px),var(--modal-max-width))]'
                            : 'sm:max-w-[450px]',
                    )}
                    style={
                        option?.maxWidth
                            ? ({
                                  ['--modal-max-width' as const]: option.maxWidth,
                              } as React.CSSProperties)
                            : undefined
                    }
                >
                    {option?.title && <Title option={option} />}
                    {children}
                    {option?.showCloseButton && <ModalCloseXButton />}
                </div>
                {option?.backdrop && (
                    <form method="dialog" className="modal-backdrop">
                        {!option?.preventClose && <button>닫기</button>}
                    </form>
                )}
            </dialog>
        );
    },
);

const Title = observer(({ option }: { option: ModalOption }) => {
    if (!option.title) {
        return null;
    }

    return (
        <h3
            className={
                option.titleAlign === 'center'
                    ? 'mx-5 mb-4 w-full shrink-0 text-center text-xl font-bold'
                    : 'mx-5 mb-4 shrink-0 text-lg font-bold'
            }
        >
            {option.title}
        </h3>
    );
});

const BodyScroll = observer(
    ({ children, className }: { children: React.ReactNode; className?: string }) => {
        return (
            <div
                className={twMerge('max-h-[calc(90vh-8rem)] overflow-auto px-5', className)}
                style={{
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehaviorY: 'contain',
                }}
            >
                {children}
            </div>
        );
    },
);

/**
 * BodyCarousel용 ViewStore
 */
class BodyCarouselViewStore {
    isHovered = false;

    constructor() {
        makeAutoObservable(this);
    }

    setHovered(hovered: boolean) {
        this.isHovered = hovered;
    }
}

/**
 * ModalCarousel - 캐러셀 레이아웃을 가진 모달
 * - CarouselProvider를 자동으로 제공
 * - 스크롤 영역과 좌/우 버튼, Dots를 포함
 * - children으로 Carousel.Slide들을 받음
 */
export const ModalCarousel = observer(
    ({ children, className }: { children: React.ReactNode; className?: string }) => {
        const viewStore = useMobxInstance(BodyCarouselViewStore);

        return (
            <CarouselProvider autoHeight={true}>
                <div
                    className={twMerge('relative', className)}
                    onMouseEnter={() => {
                        viewStore.setHovered(true);
                    }}
                    onMouseLeave={() => {
                        viewStore.setHovered(false);
                    }}
                >
                    <div
                        className="max-h-[calc(90vh-8rem)] overflow-auto px-0"
                        style={{
                            WebkitOverflowScrolling: 'touch',
                            overscrollBehaviorY: 'contain',
                        }}
                    >
                        <Carousel>{children}</Carousel>
                    </div>

                    {deviceStore.hasHoverCapability && (
                        <Carousel.PrevCircleButton
                            isShow={viewStore.isHovered}
                            className="absolute top-1/2 -left-5.5 -translate-y-1/2"
                        />
                    )}

                    {deviceStore.hasHoverCapability && (
                        <Carousel.NextCircleButton
                            isShow={viewStore.isHovered}
                            className="absolute top-1/2 -right-5.5 -translate-y-1/2"
                        />
                    )}

                    <div className="mt-4 px-5">
                        <Carousel.Dots />
                    </div>
                </div>
            </CarouselProvider>
        );
    },
);

export const ModalCarouselContainer = observer(
    ({ children, className }: { children: React.ReactNode; className?: string }) => {
        const { emblaApi } = useCarouselContext();

        return (
            <Carousel.Container className={twMerge('gap-5', className)}>
                {children}
            </Carousel.Container>
        );
    },
);

/**
 * Modal 컴포넌트 - Compound Component 패턴
 */
export const Modal = Object.assign(
    {},
    {
        Dialog,
        Title,
        BodyScroll,
        ActionButton: ModalActionButton,
        CloseXButton: ModalCloseXButton,
        ModalCarousel,
        ModalCarouselContainer,
    },
);
